import {
  buildFromStoredOrCrossSession,
  buildWwwHeartbeatOrEmpty,
  buildWwwSsoUserOrEmpty,
  isCrossSessionParam,
  buildSignupDecorationOrUndefined,
  type CrossSessionParam,
  type CrossSessionResp,
  type UserData,
  type WwwHeartbeatData,
  type WwwSsoUser,
} from '@onshape/www-inertia';
import type { CrossSessionQueryResult } from '~/sanity/types';
import SolrFq from '~/utils/solr/SolrFq';
import SolrQuery from '~/utils/solr/SolrQuery';
import { useHekateEventBus } from './useHekateEventBus';
import { useServiceEndpoints } from './useServiceEndpoints';

export type LoginStatus = 'unknown' | 'loggedin' | 'loggedout';

const isUserInit: Ref<boolean> = ref(false);
const user: Ref<WwwSsoUser | null> = ref(null);
const heartbeat: Ref<WwwHeartbeatData | null> = ref(null);
const allParams: Ref<Array<CrossSessionParam>> = ref([]);
let fetchedParams: Array<CrossSessionParam> = [];
let fetchTimeout: ReturnType<typeof setTimeout> | null = null;
const isLoggedIn: Ref<LoginStatus> = ref('unknown');
const throttlePageLoadTime = Date.now();
let throttleTargetTime = 0;

type FetchThrottleInfo = {
  lastUpdated: number;
  dateAdded: number;
  count: number;
};

const addHekateListeners = async () => {
  if (import.meta.server) {
    return;
  }
  const { addEventListener } = useHekateEventBus();

  addEventListener('request-sso-info', () => sendToHekate());
};
addHekateListeners();

const sendToHekate = async () => {
  if (import.meta.server) {
    return;
  }
  const data = buildSsoInfoForHekate({ user });
  if (isLoggedIn.value !== 'unknown') {
    data.isReady = true;
  }

  const { dispatchEvent } = useHekateEventBus();
  dispatchEvent('sso-info', { ...data });
};

/** get user data from either sanity or crossSession depending on params */
const getUserData = async () => {
  const fromParams = useRoute().query;
  const env = useEnvContext();

  if (fromParams['sso-data-query'] && (env.isDev || env.isDeveloper)) {
    console.log('getting from sanity');
    return getDataFromSanity({
      persona: `${fromParams['sso-data-query']}`,
    });
  }
  return await getDataFromCrossSession();
};

/** Query sanity for mock cross session responses depending on requested persona in params */
const getDataFromSanity = async ({
  persona,
}: {
  persona: string;
}): Promise<CrossSessionQueryResult> => {
  if (persona === 'default') return {} as CrossSessionQueryResult;

  const fq = SolrFq.and().push('_type_s', 'crossSessionResp').push('persona_s', persona);
  const q = SolrQuery.devObj().setFq(fq).setRange({ start: 0, rows: 1 });
  const { data: queryResp } = await useSolrQuery<CrossSessionQueryResult>({
    q,
  });
  if (!queryResp.value || queryResp.value.length === 0) {
    console.warn('No Solr response from sanity for persona', persona);
    return {} as CrossSessionQueryResult;
  }

  return queryResp.value[0] ?? ({} as CrossSessionQueryResult);
};

/** fetch current user data from cross session */
const getDataFromCrossSession = async (): Promise<CrossSessionQueryResult> => {
  const { mapFbFunction } = useServiceEndpoints();
  const crossSessionEndpoint = await mapFbFunction('/api/fb/crossSession');

  allParams.value.forEach((param) => {
    if (!isCrossSessionParam(param)) {
      throw new Error('Invalid SsoReqParam');
    }

    crossSessionEndpoint.searchParams.append('sso-data-query', param);
  });

  crossSessionEndpoint.searchParams.append('nonce', newNonce());

  // actually make fetch to crossSession
  const data = await useWwwFetch<CrossSessionResp>({
    url: crossSessionEndpoint,
    method: 'POST',
  });

  if (!data.content) throw new Error('No content in crossSession response');
  isLoggedIn.value = data.loginStatus as LoginStatus;

  return data.content as CrossSessionQueryResult;
};

const fetchUserData = async (): Promise<UserData> => {
  const userData: CrossSessionQueryResult = await getUserData();
  const result: UserData = {
    user: null,
    heartbeat: null,
  };

  if (userData?.User) {
    result.user = buildWwwSsoUserOrEmpty(userData.User);
  }

  if (userData?.Heartbeat) {
    result.heartbeat = buildWwwHeartbeatOrEmpty(userData.Heartbeat);
  }

  storeResp({ user: result.user, heartbeat: result.heartbeat });
  return result;
};

/** promise chain to handle debounced fetches */
let promiseChain: Promise<UserData> = Promise.resolve<UserData>({ user: null, heartbeat: null });

/** fetchUserData with debounce and params handling */
const makeAsyncFetch = async (params: Array<CrossSessionParam>) => {
  try {
    promiseChain = promiseChain.then(() => fetchUserData());
    const userData = await promiseChain;

    if (params.includes('heartbeat')) {
      heartbeat.value = userData.heartbeat;
    }

    if (userData.user) {
      if (params.includes('currentUserData')) {
        user.value = userData.user;
      }

      if (params.includes('eloquaExtraData')) {
        const currUser = user.value || buildWwwSsoUserOrEmpty(null);
        user.value = {
          ...currUser,
          eloquaExtraData: userData.user.eloquaExtraData,
        };
      }

      if (params.includes('refCodeData')) {
        const currUser = user.value || buildWwwSsoUserOrEmpty(null);
        user.value = {
          ...currUser,
          refCodeData: userData.user.refCodeData,
        };
      }

      if (params.includes('emailPreferences')) {
        const currUser = user.value || buildWwwSsoUserOrEmpty(null);
        user.value = {
          ...currUser,
          eloquaExtraData: userData.user.eloquaExtraData,
        };
      }

      if (params.includes('contactFields')) {
        const currUser = user.value || buildWwwSsoUserOrEmpty(null);
        user.value = {
          ...currUser,
          eloquaExtraData: userData.user.eloquaExtraData,
        };
      }
    }
    storeResp(userData);
  } catch (error) {
    console.error('useCrossSession error: ', error);
  }
};

/** set params for a fetch request to cross session, debounced */
const setParamsForFetch = async ({ params }: { params: Array<CrossSessionParam> }) => {
  if (import.meta.server) {
    return;
  }

  const isThrottled = await throttleCrossSessionFetch();

  params.forEach((param) => {
    if (!isCrossSessionParam(param)) {
      throw new Error('Invalid CrossSessionParam');
    }

    if (!allParams.value.includes(param)) {
      allParams.value.push(param);
    }
  });

  // Clear the existing timeout if new params are added
  if (fetchTimeout) {
    clearTimeout(fetchTimeout);
  }

  const timeoutTime = 500; // debounce timeout time
  // Set a timeout to delay the fetch
  fetchTimeout = setTimeout(() => {
    const toFetchParams = normalizeParams();
    if (toFetchParams.length === 0) {
      fetchTimeout = null;
      return;
    }
    fetchedParams = [...fetchedParams, ...toFetchParams];
    if (isThrottled) {
      // for logging, debugging and possibly future proofing
      fetchedParams.push('isThrottled');
    }

    makeAsyncFetch(toFetchParams)
      .catch((e) => {
        console.error('makeAsyncFetch - Error fetching user data:', e);
      })
      .finally(() => {
        isUserInit.value = true; // Set user init to true after fetch
      });
    fetchTimeout = null; // Reset the timeout after the fetch
  }, timeoutTime);
};

/** Conditionally throttle the first crossSession fetches, to avoid bot spam of crossSession fetches */
const throttleCrossSessionFetch = async (): Promise<boolean> => {
  if (import.meta.server) {
    return false;
  }
  if (!window.csrfToken) {
    return false;
  }

  if (throttleTargetTime != 0) {
    await new Promise((resolve) => setTimeout(resolve, throttleTargetTime - Date.now())); // Debug
    return false;
  }

  const { getFromSessionStorageOrNull, setToSessionStorage } = useWwwStorage();

  const fetchThrottleInfo = getFromSessionStorageOrNull<FetchThrottleInfo>({
    key: 'CrossSessionFetchThrottle',
  });
  if (!fetchThrottleInfo) {
    setToSessionStorage<FetchThrottleInfo>({
      key: 'CrossSessionFetchThrottle',
      data: {
        lastUpdated: Date.now(),
        dateAdded: Date.now(),
        count: 1,
      },
    });
    return false;
  }

  // reset every few minutes
  if (Date.now() - fetchThrottleInfo.dateAdded > 1000 * 60 * 3) {
    setToSessionStorage<FetchThrottleInfo>({
      key: 'CrossSessionFetchThrottle',
      data: {
        lastUpdated: Date.now(),
        dateAdded: Date.now(),
        count: 1,
      },
    });
    return false;
  }

  setToSessionStorage<FetchThrottleInfo>({
    key: 'CrossSessionFetchThrottle',
    data: {
      lastUpdated: Date.now(),
      dateAdded: fetchThrottleInfo.dateAdded || Date.now(),
      count: fetchThrottleInfo.count + 1,
    },
  });

  if (fetchThrottleInfo.count < 5) {
    return false;
  }

  // only throttle early requests
  if (throttleTargetTime === 0) {
    const waitTime = (fetchThrottleInfo.count - 4) * 200; // add 200ms per extra request
    throttleTargetTime = throttlePageLoadTime + waitTime;
  }

  // too late in page load time to start throttling
  if (throttleTargetTime < Date.now()) {
    return false;
  }

  console.log(
    `Throttling - Current time: ${new Date().toLocaleString()}, throttle target time: ${new Date(throttleTargetTime).toLocaleString()}`
  );
  await new Promise((resolve) => setTimeout(resolve, throttleTargetTime - Date.now())); // Debug
  return true;
};

/** get the params we need to fetch, removing ones we already have */
const normalizeParams = (): Array<CrossSessionParam> => {
  const curParams = allParams.value;
  // if we need heartbeat, just get whatever
  if (heartbeat.value === null) {
    return curParams;
  }

  // filter all params that we already have data for
  const tmpParams = curParams.filter((param) => {
    return fetchedParams.indexOf(param) === -1;
  });

  if (tmpParams.length === 0) {
    tmpParams.push('heartbeat'); // always fetch heartbeat if we have no new params
  }
  return tmpParams;
};

let isInit = false;
const init = () => {
  if (isInit) return;
  isInit = true;

  // if !heartbeat or is too old, fetch heartbeat
  const heartBeatExpires = 1000 * 60 * 60;
  const { heartbeatStorable } = useWwwStorage();
  const heartbeatDataFromStorage = heartbeatStorable.value;
  if (
    !heartbeatDataFromStorage ||
    !heartbeatDataFromStorage.lastFetched ||
    heartbeatDataFromStorage.lastFetched < Date.now() - heartBeatExpires
  ) {
    setParamsForFetch({ params: ['heartbeat'] });
  } else if (heartbeatDataFromStorage.csrfToken) {
    const { csrfToken } = heartbeatDataFromStorage;
    // note this assume the user's clock is correct, which isn't always true at all
    const [, dateAdded] = csrfToken.split('-');
    const dateAddedNum = Number(dateAdded);
    if (dateAddedNum + 1000 * 60 * 60 > Date.now()) {
      window.csrfToken = csrfToken;

      useHekateEventBus().dispatchEvent('form-inject-value', {
        fieldName: 'csrfToken',
        fieldValue: csrfToken,
      });
    } else {
      console.log('hearbeat csrfToken is expired, fetching new one');
    }
  }
};

//** store user data resp to local storage */
const storeResp = (resp: UserData) => {
  const { personalizationProfileStorable, heartbeatStorable } = useWwwStorage();

  const fromStorage = personalizationProfileStorable.value;

  let newDec;
  if (isLoggedIn.value === 'loggedin') {
    newDec = buildSignupDecorationOrUndefined({
      ...fromStorage?.signupDecoration,
      stage: 'have-logged-in',
    });
  }

  const profileToStore = buildFromStoredOrCrossSession({
    fromStorage,
    fromCrossSession: {
      user: resp.user && {
        ...resp.user,
        _id: resp.user._id || '',
        lastFetched: Date.now(),
        signupDecoration: newDec || fromStorage?.signupDecoration,
      },
      heartbeat: resp.heartbeat && { ...resp.heartbeat, lastFetched: Date.now() },
    },
  });

  heartbeatStorable.value = profileToStore.heartbeatData;
  personalizationProfileStorable.value = profileToStore;
};

const refreshHeartbeat = () => {
  heartbeat.value = null;
  window.csrfToken = '';
  const { clearHeartbeatData } = useWwwStorage();
  clearHeartbeatData();
  clearParams();
  setParamsForFetch({ params: ['heartbeat'] });
};

const clearParams = () => {
  allParams.value = [];
};

export const useCrossSession = () => {
  init();

  watch(user, () => {
    sendToHekate();
  });

  return {
    isUserInit,
    user,
    heartbeat,
    allParams,
    setParamsForFetch,
    refreshHeartbeat,
    isLoggedIn,
  };
};
